import { useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useTabsPallet from './useTabsPallet'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications';
import { Trucks } from '../../../../../../modules/API/Distribution/trucks';
import useDataFetcher from '../../../knisot/hooks/useFetchData';
import { IPalletByKavItem } from '../../../../../../modules/API/Distribution/models/palletByKav';
import { format } from 'date-fns';
import { IPutAreaLoad, IPutPalletStatus } from '../../../../../../modules/API/Distribution/models/fetchData';
import { useDispatch } from 'react-redux';
import { LOAD, LOAD_END } from '../../../../../../../store/actions/actionsTypes';
import { LINKS } from '../../../../../../Routs/config';

interface IRef {
  focusInput: () => void;
  blurInput: () => void;
}

const useKavPalletService = (date: Date) => {
    const {id} = useParams() as {id: string}
    const {TAB_LIST, handleSelectTab, activeTab} = useTabsPallet()
    const inputRef = useRef<IRef | null>(null)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [searchValue, setSearchValue] = useState<string>('')

    const {
      data: palletList,
      setData: setPalletList,
      isLoading: loadingPalletList,
  } = useDataFetcher(
      Trucks.getPalletListByKav, `${id}/pallets?Date=${format(date, 'yyyyMMdd')}`,
  )

   
    const handleScan = () => {
      if (!palletList) return;
      
      // Validate that searchValue is only digits
      const digitsRegex = /^\d+$/;
      if (!digitsRegex.test(searchValue)) {
          handleNotifications("מספר משטח לא תקין", { type: "warning" });
        return;
      }
      
      const palletNum = Number(searchValue);
      if (isNaN(palletNum)) return;
      
      const foundPallet = palletList.find((item) => item.Pallet_Num === palletNum);
      
      if (!foundPallet) {
        handleNotifications("משטח לא נמצא", { type: "warning" });
        return;
      }
      // Calculate the lowest Distr_Order among pallets not yet loaded (Status !== 7)
      const lowestDistrOrder = palletList
        .filter((item) => item.Status !== 7)
        .reduce((min, item) => item.Distr_Order < min ? item.Distr_Order : min, Number.MAX_SAFE_INTEGER);
      

      if (foundPallet.Status === 7) {
        handleNotifications("משטח כבר הועמס", { type: "warning" });
        return;
      }
   
      if (foundPallet.Distr_Order > lowestDistrOrder) {
        handleNotifications("סדר העמסה לא תקין", { type: "warning" });
        return;
      }
      
      const updatedPalletList = palletList.map(item => 
        item.Pallet_Num === foundPallet.Pallet_Num 
          ? { ...item, Status: 7 } 
          : item
      );
      
      setPalletList(updatedPalletList);
      handleNotifications("משטח הועמס", { type: "success" });
      handleSelectTab(TAB_LIST[0]);
      setSearchValue('');
    };

    const loadPallet = (palletNum: number) => {
      if(!palletList) return;
      const updatedPalletList = palletList.map(item => 
        item.Pallet_Num === palletNum 
          ? { ...item, Status: 7 } 
          : item
      );
      setPalletList(updatedPalletList);
      handleNotifications("משטח הועמס", { type: "success" });
    }
    const unloadPallet = (palletNum: number) => {
      if(!palletList) return;
      const updatedPalletList = palletList.map(item => 
        item.Pallet_Num === palletNum 
          ? { ...item, Status: 0 } 
          : item
      );
      setPalletList(updatedPalletList);
      handleNotifications("משטח הוסר מהעמסה", { type: "success" });
    }

    const isReadyToFinish = useMemo(() => {
      if (!palletList) return false;
      return palletList.every(item => item.Status === 7);
    }, [palletList]);
   
    const putPalletStatus = async (data: IPutPalletStatus) => {
      try {
        await Trucks.putPalletStatus(data);
        data.Status === 7 ? loadPallet(data.Pallet_Num) : unloadPallet(data.Pallet_Num);
      } catch (error) {
        handleNotifications("שגיאה בעדכון מצב המשטח", { type: "error" });
      
      }
    }
    const handleFinish = async () => {
      if (!palletList) return;
      if (!isReadyToFinish) handleNotifications("לא כל המשטחים הועמסו", { type: "warning" });
      try {
        const data: IPutAreaLoad = {
          AreaID: +id,
          Date: format(date, 'yyyyMMdd'),
          Loaded: true
        }
        dispatch({type: LOAD})
        await Trucks.areaLoad(data)
        handleNotifications("העמסה הושלמה", { type: "success" });
        navigate(LINKS.afaca.loading.mainURL)
      } catch (error) {
        handleNotifications("שגיאה בהשלמת העמסה", { type: "error" });
      } finally {
        dispatch({type: LOAD_END})

      }
       
      handleNotifications("העמסה הושלמה", { type: "success" });
    }

    const render = useMemo(() => {
      if (!palletList) return []
      return palletList.filter(item => {

          return item.Pallet_Num.toString().includes(searchValue) &&
              (activeTab === TAB_LIST[2] ? true : activeTab === TAB_LIST[0] ? item.Status === 7 : item.Status !== 7)
      })
  }, [searchValue, palletList, activeTab])

  return (
    {
      id,
      TAB_LIST,
      activeTab,
      handleSelectTab,
      inputRef,
      searchValue,
      setSearchValue,
      handleScan,
      isReadyToFinish,
      handleFinish,
      putPalletStatus,
      render
    }
  )
}

export default useKavPalletService